<template>
  <v-container fluid pa-0 class="search-wrapper">
    <div ref="searchField" class="search-field-wrap">
      <button class="search-icon-btn" @click.stop="sendToSearch">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
      </button>
      <span
        v-if="selectedMention"
        class="user-mention selected-mention-chip"
        :data-user-id="selectedMention.id"
        :data-user-name="selectedMention.label"
        :aria-label="`Usuario seleccionado: ${selectedMention.label}`"
      >
        @{{ selectedMention.label }}
        <button
          type="button"
          class="mention-remove"
          title="Quitar usuario"
          aria-label="Quitar usuario"
          @click.stop="closeResults"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </span>
      <input
        v-else
        ref="searchInput"
        class="search-input"
        type="text"
        placeholder="Buscar tareas, RDI, adicionales…"
        v-model="searchInput"
        @keydown="handleSearchKeydown"
      />
      <transition name="fade">
        <button v-if="hasSearchValue" class="clear-btn" @click.stop="closeResults">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </transition>
    </div>

    <MentionPicker
      ref="userMentionPicker"
      :visible="showUserPicker"
      :x="mentionPickerX"
      :y="mentionPickerY"
      :project-id="selectedProjectId"
      :types="['users']"
      :query="mentionQuery"
      @select="selectMention"
      @close="closeMentionPicker"
    />

    <div class="search-dropdown-wrap" :class="{ 'mobile': $vuetify.breakpoint.mobile }">
      <transition name="slide-down">
        <SearchBlock
          v-if="showResults"
          :searching="searching"
          :searchResult="searchResult"
          :closeResults="closeResults"
          :applyFilters="handleApplyFilters"
        />
      </transition>
    </div>
  </v-container>
</template>

<script>
import { search } from '@/helpers/api/general'
import SearchBlock from './SearchBlock'
import MentionPicker from '../Helpers/MentionPicker'

export default {
  name: 'SearchBarHeader',
  components: { SearchBlock, MentionPicker },
  data: () => ({
    searchInput: '',
    selectedMention: null,
    showUserPicker: false,
    mentionQuery: '',
    mentionPickerX: 0,
    mentionPickerY: 0,
    showResults: false,
    searching: false,
    searchResult: {},
    filters: [],
    types: []
  }),
  computed: {
    selectedProjectId() {
      return this.$store.projectsStore.state.selectedProject?.id || null
    },
    effectiveSearch() {
      return this.selectedMention
        ? `@${this.selectedMention.label}`
        : this.searchInput
    },
    hasSearchValue() {
      return Boolean(this.selectedMention || this.searchInput)
    }
  },
  watch: {
    searchInput(value) {
      const input = value || ''
      if (!input.startsWith('@')) {
        this.closeMentionPicker()
        return
      }

      this.mentionQuery = input.slice(1)
      this.showUserPicker = true
      this.$nextTick(this.positionMentionPicker)
    }
  },
  mounted() {
    window.addEventListener('resize', this.positionMentionPicker)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.positionMentionPicker)
  },
  methods: {
    closeResults() {
      this.searchInput = ''
      this.selectedMention = null
      this.closeMentionPicker()
      this.showResults = false
      this.searching = false
      this.searchResult = []
      this.filters = []
      this.types = []
    },
    closeMentionPicker() {
      this.showUserPicker = false
      this.mentionQuery = ''
    },
    positionMentionPicker() {
      if (!this.showUserPicker || !this.$refs.searchField) return
      const rect = this.$refs.searchField.getBoundingClientRect()
      this.mentionPickerX = rect.left
      this.mentionPickerY = rect.bottom + 6
    },
    handleSearchKeydown(event) {
      if (this.showUserPicker && ['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(event.key)) {
        event.preventDefault()
        event.stopPropagation()
        const picker = this.$refs.userMentionPicker
        if (!picker) return
        if (event.key === 'ArrowDown') picker.highlightNext()
        else if (event.key === 'ArrowUp') picker.highlightPrev()
        else if (event.key === 'Enter') {
          if (picker.totalCount > 0) picker.selectHighlighted()
          else this.searchForString(this.effectiveSearch)
        }
        else this.closeMentionPicker()
        return
      }

      if (event.key === 'Enter') this.searchForString(this.effectiveSearch)
    },
    selectMention(item) {
      this.selectedMention = item
      this.searchInput = ''
      this.closeMentionPicker()
      this.searchForString(this.effectiveSearch)
    },
    sendToSearch(event) {
      if (!event || event.type === 'click' || event.key === 'Enter') {
        this.searchForString(this.effectiveSearch)
      }
    },
    handleApplyFilters(filters, types) {
      this.filters = filters
      this.types = types
      if (this.effectiveSearch && this.effectiveSearch.trim()) {
        this.searchForString(this.effectiveSearch)
      }
    },
    async searchForString(searchString) {
      if (!searchString || !searchString.trim()) return
      this.searching = true
      this.showResults = true
      const data = {
        search: searchString,
        project_id: this.$store.projectsStore.state.selectedProject.id,
        filters: this.filters,
        types: this.types
      }
      const searchResult = await search(data)
      if (searchResult.code === 200) {
        this.searchResult = searchResult
      }
      this.searching = false
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap');

.search-wrapper {
  position: relative;
  font-family: 'DM Sans', sans-serif;
}

/* ── Input Field ── */
.search-field-wrap {
  position: relative;
  display: flex;
  align-items: center;
  background: rgba(45, 75, 149, 0.05);
  border: 1px solid rgba(45, 75, 149, 0.8);
  border-radius: 12px;
  backdrop-filter: blur(8px);
  transition: background 0.2s, border-color 0.2s, box-shadow 0.2s;
  overflow: hidden;
}

.search-field-wrap:focus-within {
  background: rgba(45, 75, 149, 0.10);
  border-color: rgba(45, 75, 149, 0.8);
  box-shadow: 0 0 0 3px rgba(94, 133, 239, 0.25);
}

.search-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  background: transparent;
  border: none;
  color: #2D4B95;
  cursor: pointer;
  transition: color 0.15s;
  padding: 0 0 0 10px;
}
.search-icon-btn:hover { color: #1e3860; }

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  padding: 8px 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: #2D4B95 !important;
  caret-color: #2D4B95;
}

.search-input::placeholder {
  color: rgba(45, 75, 149, 0.7) !important;
}

.selected-mention-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  max-width: calc(100% - 46px);
  margin: 0 6px 0 6px;
  margin-right: auto;
  padding: 4px 6px 4px 8px;
  border: 1px solid rgba(45, 75, 149, 0.2);
  border-radius: 6px;
  background: rgba(94, 133, 239, 0.14);
  color: #2D4B95;
  font-size: 13px;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.selected-mention-chip .mention-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #2D4B95;
  cursor: pointer;
}

.selected-mention-chip .mention-remove:hover {
  background: rgba(45, 75, 149, 0.12);
}

.clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-right: 5px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: #2D4B95 !important;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s;
}
.clear-btn:hover {
  background: rgba(45, 75, 149, 0.1) !important;
  color: #1e3860 !important;
}

.clear-btn svg,
.clear-btn svg * {
  stroke: #2D4B95 !important;
  color: #2D4B95 !important;
}

/* ── Dropdown ── */
.search-dropdown-wrap {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 420px;
  z-index: 999;
}
.search-dropdown-wrap.mobile {
  left: 0;
  width: 100%;
  padding: 0 4px;
}

/* ── Transitions ── */
.slide-down-enter-active { transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1); }
.slide-down-leave-active { transition: all 0.15s ease-in; }
.slide-down-enter { opacity: 0; transform: translateY(-8px); }
.slide-down-leave-to { opacity: 0; transform: translateY(-4px); }

.fade-enter-active, .fade-leave-active { transition: opacity 0.15s; }
.fade-enter, .fade-leave-to { opacity: 0; }
</style>